import type { SpacePlanningTranslations } from "../en/SpacePlanning";
import type { TranslationShape } from "../../store/types";

export const spacePlanningEs: TranslationShape<SpacePlanningTranslations> = {
  pageTitle: "Planificación de Espacios",
  pageSubtitle:
    "Revisa, edita y aprueba planogramas pendientes, visualiza planogramas activos o consulta registros históricos",
  viewerPageSubtitle:
    "Visualiza los planogramas entregados que aún no entran en vigor, el activo en cada tienda o los anteriores",

  banner: {
    title: "Da Vida a Tus Planogramas",
    description:
      "Gestiona el ciclo de vida completo del planograma en un solo lugar. Revisa los planogramas pendientes generados a partir de tus simulaciones de surtido previas, valida sus métricas de rendimiento y edítalos según sea necesario antes de aprobarlos para activación — o monitorea los planogramas activos en todas tus tiendas. Usa filtros y acciones masivas para optimizar tu flujo de trabajo y avanzar más rápido de la decisión al despliegue.",
  },

  tabs: {
    pending: "Planogramas Pendientes",
    active: "Planogramas Activos",
    historical: "Planogramas Históricos",
  },

  filters: {
    viewOn: "Ver en:",
    allChains: "Todas las Cadenas",
    allStores: "Todas las Tiendas",
    allCategories: "Todas las Categorías",
    searchPlaceholder: "Buscar por tienda, ID o SIM-id...",
    filterPogs: "Filtrar POGs por:",
    latest: "Más reciente",
    oldest: "Más antiguo",
    activeFilters: "Filtros activos:",
    clearAll: "Limpiar todo",
    commercialChain: "Cadena Comercial",
    store: "Tienda",
    category: "Categoría",
    assortment: "Surtido",
    search: "Búsqueda",
    searchButton: "Buscar",
    scoreBelow: "Mostrar planogramas con puntuación por debajo de",
    scoreBelowTag: "{{score}} por debajo de {{threshold}}",
  },

  pagination: {
    rowsPerPage: "Filas por página:",
  },

  results: {
    showing: "Mostrando {{start}}-{{end}} de {{total}} tiendas",
    selected: "({{count}} seleccionadas)",
  },

  columns: {
    storeCode: "Código de Tienda",
    pogName: "Nombre POG",
    storeStatus: "Estado de Tienda",
    pogId: "ID POG",
    category: "Categoría",
    goLiveDate: "Fecha de Lanzamiento",
    assortment: "Surtido",
    returnOnSpace: "Retorno de Espacio",
    coreRange: "Rango Principal",
    shelfAlignment: "Alineación de Estante",
    dogms: "DOGMS",
    aesthetics: "Estética",
    size: "Tamaño",
    warnings: "Alertas",
    lastEdited: "Última Edición",
    archivedAt: "Archivado",
    actions: "Acciones",
    view: "Ver",
    analysis: "Análisis",
    logs: "Registros",
  },

  status: {
    pending: "Pendiente",
    active: "Activo",
    historical: "Histórico",
    notAvailable: "No disponible",
    notScored: "Sin puntuar",
    goLiveNotSet: "Sin definir",
    delivered: "Entregado",
    deliveredDetail: "Entregado a la tienda; entra en vigor en su fecha de lanzamiento",
    loading: "Cargando planogramas...",
    loadError: "No se pudieron cargar los planogramas.",
    loadErrorToastTitle: "Error al cargar datos de planificación de espacios",
    loadErrorToastMessage: "No se pudieron cargar estos planogramas: {{sources}}.",
    processing: "Procesando...",
    error: "Error al procesar",
    updateFailed: "Actualización fallida",
    updateFailedDetail:
      "La actualización se ejecutó pero ninguna de las inserciones, eliminaciones o intercambios aprobados se aplicó. El planograma refleja el diseño original, no los cambios aprobados.",
    updatePartial: "Aplicada parcialmente",
    updatePartialDetail:
      "La actualización se ejecutó y algunos de los cambios aprobados (inserciones, eliminaciones, intercambios) se aplicaron. Otros fueron omitidos (sin anaquel compatible, impacto en PNL u otras restricciones de Frogger).",
  },

  actions: {
    update: "Actualizar",
    approve: "Aprobar",
    edit: "Editar",
    viewEdit: "Ver / Editar",
    reviewAll: "Revisar todo ({{count}})",
    updateSelected: "Actualizar Seleccionados ({{count}})",
    approveSelected: "Aprobar Seleccionados ({{count}})",
    deleteSelected: "Eliminar Seleccionados ({{count}})",
    violations: "Violaciones ({{count}})",
    reports: "Reportes ({{count}})",
    storeIntegrityReport: "Reporte de Integridad de Tienda",
    categoryRangeReviewReport: "Reporte de Revisión de Rango por Categoría",
    export: "Exportar ({{count}})",
  },

  toasts: {
    approvalsSuccess: "Planogramas aprobados",
    approvalsError: "No se pudieron aprobar los planogramas",
    missingPogReference: "Falta la referencia de merchflow/POG para esta fila",
    pogDataUnavailable: "Los datos del POG no están disponibles",
  },

  noResults: "No se encontraron planogramas",

  logs: {
    title: "Registro de Actividad",
    exportLogs: "Exportar Registros",
    entries: {
      activated: "Planograma Activado",
      analysisRun: "Análisis de Rendimiento Ejecutado",
      compliancePassed: "Verificación de Cumplimiento Aprobada",
      warningDetected: "Alerta Detectada",
      auditCompleted: "Auditoría de Tienda Completada",
    },
  },

  preview: {
    title: "Vista Previa del Planograma",
    category: "Categoría",
    assortment: "Surtido",
    activeDate: "Fecha de Activación",
    size: "Tamaño",
    segments: "{{count}} segmentos",
    performanceMetrics: "Métricas de Rendimiento",
    status: "Estado",
    returnOnSpace: "Retorno de Espacio",
    coreRange: "Rango Principal",
    shelfAlignment: "Alineación de Estante",
    dogms: "DOGMS",
    aesthetics: "Estética",
    planogramLayout: "Diseño del Planograma",
    shelf: "Estante {{number}}",
    section: "Sección {{brand}}",
    totalSegments: "Total de Segmentos",
    totalSkus: "Total de SKUs",
    close: "Cerrar",
    export: "Exportar",
    editPlanogram: "Editar Planograma",
  },

  history: {
    title: "Historial del Planograma",
    approvedBy: "Aprobado por:",
    activated: "Activado:",
    deactivated: "Desactivado:",
    close: "Cerrar",
    exportHistory: "Exportar Historial",
  },

  confirmUpdate: {
    singleTitle: "Confirmar Actualización del Planograma",
    singleSubtitle: "Revisa los cambios antes de continuar",
    bulkTitle: "Confirmar Actualización Masiva",
    bulkSubtitle: "Estás a punto de actualizar {{count}} planogramas",
    bulkSummary: "Resumen de Actualización Masiva",
    bulkSummaryDesc: "{{count}} planogramas serán activados",
    whatWillHappen: "Qué sucederá:",
    step1Title: "Activar nuevo planograma",
    step1Desc: "La versión {{id}} será establecida como Activa",
    step2Title: "Actualizar planograma de tienda",
    step2Desc: "El planograma será marcado como aprobado y activo en Planogramas",
    step3Title: "Archivar versión anterior",
    step3Desc: "El planograma activo actual será archivado (si existe)",
    step4Title: "Remover de pendientes",
    step4Desc: "Este planograma será removido de la cola de Planificación de Espacios",
    planogramDetails: "Detalles del Planograma",
    categoryLabel: "Categoría:",
    sizeLabel: "Tamaño:",
    sizeSegments: "{{count}} segmentos",
    activeDateLabel: "Fecha de Activación:",
    endDateLabel: "Fecha de Fin:",
    selectedPlanograms: "Planogramas Seleccionados ({{count}}):",
    warningTitle: "Esta acción no se puede deshacer",
    warningDesc:
      "Una vez activado, este planograma reemplazará inmediatamente al actual y afectará las operaciones de la tienda.",
    warningDescBulk:
      "Una vez activados, estos {{count}} planogramas reemplazarán inmediatamente a los actuales y afectarán las operaciones de las tiendas.",
    cancel: "Cancelar",
    confirm: "Confirmar Actualización",
    confirmBulk: "Confirmar Actualización ({{count}})",
    na: "N/A",
  },

  confirmDelete: {
    title: "Eliminar Planogramas Seleccionados",
    subtitle: "Estás a punto de eliminar {{count}} planograma(s) pendiente(s)",
    selectedPlanograms: "Planogramas Seleccionados ({{count}}):",
    warningTitle: "Esta acción no se puede deshacer",
    warningDesc:
      "Estos {{count}} planograma(s) se eliminarán de la cola de Planeación de Espacio. Los planogramas que se estén procesando se omitirán.",
    cancel: "Cancelar",
    confirm: "Eliminar Seleccionados ({{count}})",
    toasts: {
      deletedWithSkipped:
        "Se eliminaron {{deleted}} planograma(s), se omitieron {{skipped}} en proceso",
      deleted: "Planogramas eliminados",
      failed: "Error al eliminar los planogramas",
      couldNotDelete:
        "No se pudieron eliminar {{count}} planograma(s) — falta la referencia del planograma",
    },
  },

  review: {
    prepared: {
      pendingStorageUnavailable:
        "El almacenamiento de sesión del navegador no está disponible. Actívalo antes de aplicar para poder recuperar un envío interrumpido. No se ha enviado una solicitud nueva.",
      invalidPending:
        "La solicitud pendiente guardada no es válida. Actualiza el estado de las tareas guardadas y revisa las propuestas actuales antes de iniciar otra solicitud.",

      loadingOutcomes: "Cargando resultados por tienda…",
      outcomesFailed:
        "No se pudieron actualizar los resultados por tienda. Los recuentos guardados de la tarea siguen siendo válidos.",

      checkingIssues: "Comprobando los problemas del plano…",
      queueRefreshFailed:
        "No se pudieron actualizar los planos actuales. Actualiza el estado antes de revisar otra propuesta.",
      scanWholeFlow:
        "La cobertura del cálculo corresponde a todo el merchflow. La revisión y aplicación se limitan a la cadena seleccionada.",
      scanUpdated: "Última actualización del cálculo {{time}}",

      chooseFlow: "Elige un merchflow para revisar",
      chooseFlowDetail:
        "Elige un merchflow antes de preparar, revisar o aplicar cambios. Se conservan los filtros de categoría y cadena.",
      flowLoadFailed:
        "No se pudieron cargar los merchflows disponibles. Reintenta para elegir el alcance.",
      flowOption: "Merchflow {{id}} · {{count}} tiendas pendientes",

      submitting: "Enviando cambios capturados…",
      queueing: "Añadiendo cálculo a la cola…",
      refreshStatus: "Actualizar estado",
      scan: "Cálculo: {{completed}} / {{eligible}} finalizados · {{unscanned}} sin calcular · {{queued}} en cola · {{scanning}} analizando · {{calculating}} calculando · {{failed}} fallidos · {{seconds}}s transcurridos",
      proposalRefreshError:
        "No se pudo actualizar las propuestas. Los resultados guardados siguen disponibles.",
      jobRefreshError: "No se pudo actualizar el estado. Los resultados aplicados no han cambiado.",
      savedCanvas: "Plano guardado actual",
      originalCanvas: "Plano original",
      preparedCanvas: "Propuesta preparada",
      savedScore: "Puntaje del plano guardado actual",
      showAll: "Mostrar todas las tiendas",
      recover: "Recuperar solicitud pendiente",
      pending: "Propuestas capturadas pendientes: {{ids}}",
      tally: "{{nothingSafe}} sin cambios seguros · {{preparing}} preparando · {{failed}} fallidas",
      scanFailed: "Falló la preparación de esta tienda",
      scanFailedUnknown:
        "El servidor no pudo preparar este plano. Reintenta para solicitar una nueva preparación.",
      retryScan: "Reintentar preparación de esta tienda",
      prepare: "Preparar de nuevo",
      job: "Aplicación · {{status}} · {{done}}/{{total}}",
      cancel: "Cancelar elementos pendientes",
      retry: "Reintentar solo elementos fallidos",
      outcomes: "Resultados por tienda",
      plan: "Plano {{id}}",
      unavailable: "no disponible",
      stale: "Prepara y revisa una nueva propuesta antes de aplicar.",
      actionError: "La acción falló. Actualiza el estado antes de reintentar.",
      noSafeNotes: "Por qué no se preparó un cambio seguro",
      queuedToast: "Cambios preparados en cola. El progreso se guarda en el servidor.",
      retrySubmission: "Reintenta para recuperar la misma solicitud.",
      undoneToast: "Corrección deshecha: el plano vuelve a como estaba. Rehacer la devuelve.",
      prepareFirst: "Preparar correcciones",
      narrowed: "El reproductor muestra solo los planos que eligió.",
      notice: {
        title: "Correcciones listas",
        body: "{{ready}} listas para revisar · {{nothingSafe}} sin corrección segura · {{failed}} fallidas",
        ask: "Avisarme cuando termine",
        on: "Recibirá una notificación del navegador cuando termine la preparación.",
        blocked:
          "Las notificaciones del navegador están bloqueadas para este sitio; el título de la pestaña indicará cuando termine.",
      },
      steps: {
        details: "Detalles: estado del análisis, historial y todo lo demás",
      },
      elapsedRunning: "En curso desde hace {{duration}} · última actualización {{time}}",
      history: "Historial de aplicaciones ({{count}})",
      elapsedReadable: "Duró {{duration}} · última actualización {{time}}",
      duration: {
        hours: "{{hours}} h {{minutes}} min",
        minutes: "{{minutes}} min {{seconds}} s",
        seconds: "{{seconds}} s",
      },
      status: {
        idle: "Aún no hay correcciones preparadas",
        idleDetail:
          "La preparación revisa en el servidor cada plano pendiente de este merchflow. Suele tardar varios minutos y puede salir y volver.",
        waiting_one: "Esperando a que el servidor empiece · 1 tienda en cola",
        waiting_other: "Esperando a que el servidor empiece · {{count}} tiendas en cola",
        waitingDetail:
          "En cola desde hace {{duration}}. El cálculo empieza cuando haya un trabajador libre.",
        working: "Preparando correcciones · {{done}} de {{total}} tiendas revisadas",
        workingDetail:
          "{{inProgress}} calculando · {{queued}} en espera · en curso desde hace {{duration}}",
        delayed: "La preparación puede estar detenida",
        delayedDetail:
          "Sin progreso desde hace {{duration}}. Los resultados ya preparados siguen disponibles; actualice el estado para comprobarlo.",
        done: "Todas las tiendas revisadas ({{total}})",
        donePartial: "{{done}} de {{total}} tiendas revisadas",
        doneDetail: "Terminó en {{duration}}.",
        doneWithFailures:
          "{{done}} de {{total}} tiendas revisadas · {{failed}} no se pudieron preparar",
        doneWithFailuresDetail: "Abra una tienda que falló para reintentar su preparación.",
        reviewFailed: "Abrir tiendas que fallaron ({{count}})",
        unscanned_one: "1 tienda aún sin revisar; prepare de nuevo para incluirla",
        unscanned_other: "{{count}} tiendas aún sin revisar; prepare de nuevo para incluirlas",
        lastUpdate: "última actualización hace {{duration}}",
        meanwhile:
          "Ya puede revisar las tiendas listas. El progreso se guarda en el servidor, así que puede salir de esta página y volver.",
        results:
          "{{ready}} listas para revisar · {{nothingSafe}} sin corrección segura · {{failed}} fallidas · {{notNeeded}} no necesitan corrección",
        meanwhileNothingYet:
          "Las tiendas aparecen aquí para revisar en cuanto cada una está lista. El progreso se guarda en el servidor, así que puede salir de esta página y volver.",
        technical: "Detalles técnicos",
      },
      decision: {
        cancel: "Cancelar",
      },
      outcome: {
        applying: "Aplicando correcciones · {{done}} de {{total}} tiendas hechas",
        applyingDetail: "Se guarda en el servidor, así que puede salir de esta página.",
        applied_one: "Cambios aplicados a {{count}} tienda",
        applied_other: "Cambios aplicados a {{count}} tiendas",
        noneApplied: "No se aplicó ningún cambio",
        undone_one: "1 tienda se deshizo después y está como antes",
        undone_other: "{{count}} tiendas se deshicieron después y están como antes",
        loading: "Cargando resultados por tienda…",
        stillBlocked_one: "1 plano aplicado aún tiene problemas que impiden la aprobación",
        stillBlocked_other:
          "{{count}} planos aplicados aún tienen problemas que impiden la aprobación",
        allClear:
          "Ninguna puntuación preparada de los planos aplicados tiene problemas que impidan la aprobación",
        stale: "{{count}} sin aplicar porque el plano cambió después de preparar su corrección",
        failed: "{{count}} no se pudieron guardar",
        skipped: "{{count}} omitidas",
        cancelled: "{{count}} canceladas",
        nothingSafe_one: "1 tienda no tenía una corrección automática segura y no se cambió",
        nothingSafe_other:
          "{{count}} tiendas no tenían una corrección automática segura y no se cambiaron",
        notApproved:
          "Aplicar una corrección no aprueba el plano. Revise los planos aplicados antes de aprobarlos; cada uno se puede deshacer desde su plano.",
        reviewApplied: "Revisar planos aplicados ({{count}})",
        reviewBlocked: "Revisar planos aún bloqueados ({{count}})",
        reviewUnchanged: "Revisar planos sin cambios ({{count}})",
        reviewNothingSafe: "Revisar tiendas sin corrección segura ({{count}})",
        dismiss: "Descartar",
      },
      statuses: {
        queued: "en cola",
        applying: "aplicando",
        kept: "aplicado",
        stale: "desactualizado",
        skipped: "omitido",
        failed: "fallido",
        cancelled: "cancelado",
        complete: "completo",
      },
    },
    title: "Reproductor de Revisión",
    subtitle:
      "Califica, agrupa y reproduce cada planograma pendiente; marca ahora, corrige por motivo después",
    back: "Volver a Planificación de Espacios",
    loading: "Cargando la cola de revisión…",
    scoring: "Calificando {{done}} de {{total}} planogramas…",
    scopeMerchflow: "Merchflow {{id}}",
    scopeCount: "{{count}} planogramas",
    empty: "Nada que revisar",
    endOfQueue: "Fin de la cola",
    keys: "Espacio reproducir/pausar · A aprobar · F marcar · D describir · Ctrl+Z deshacer · ← → mover · V vista de comparación",
    bands: {
      fix: "Corregir",
      review: "Revisar",
      audit: "Muestra de auditoría",
      recheck: "Volver a revisar",
      recheckAgent: "Revisar · edición del agente",
      unsampled: "sin muestrear",
    },
    gradingFailed: {
      approveBlocked:
        "No se pudo calificar este plano: no se cargó su versión de CDT o la solicitud enviada. Recarga la página antes de aprobarlo.",
    },
    scoreOutdated: {
      approveBlocked:
        "Este plan se está calificando con el evaluador actual. Inténtalo de nuevo en un momento.",
      counterHint:
        "Ordenados por la puntuación de un evaluador anterior hasta que el backfill los vuelva a puntuar. Cada uno se califica de nuevo cuando está en pantalla.",
    },
    counters: {
      approved: "aprobados",
      flagged: "marcados",
      described: "descritos",
      left: "restantes",
      unsaved: "sin guardar",
      outdated: "puntuaciones antiguas",
      elapsed: "Transcurrido {{time}} · fin ≈ {{eta}}",
    },
    session: {
      saveFailed: "No se pudieron guardar algunas decisiones de revisión.",
      queueFailed: "No se pudo cargar la cola de revisión.",
    },
    problemGroups: {
      underDos: "Por debajo del mínimo de días de suministro",
      coreRange: "Faltan productos del surtido esencial",
      fit: "Ajuste físico",
      checks: "Comprobaciones no disponibles",
      fitBands: "Reglas de ubicación en estantes",
      sequenceByBase: "Orden de productos",
      tidyBlocking: "Grupos de productos separados",
      fillGaps: "Espacios en estantes",
      range: "Productos sin ubicar",
      other: "Otras comprobaciones",
      ready: "Sin problemas marcados",
    },
    draftChecks: {
      checking: "Comprobando el plano propuesto…",
      wait: "Espera a que terminen las comprobaciones antes de aplicar.",
      invalid: "No se pudo comprobar la vista previa de IA.",
      incomplete:
        "La calificación está incompleta: la puntuación y las comprobaciones no están disponibles.",
      unverified: "No se pudo verificar la solicitud de generación guardada o la versión.",
      summary:
        "Puntuación {{beforeScore}} → {{afterScore}} · {{before}} → {{after}} comprobaciones{{introduced}}",
      introduced: " · {{count}} nuevas para revisar",
      physical: "Esta corrección no cabe físicamente: {{issues}}. Pide a la IA que la revise.",
      waitForSaved: "Espera a que se cargue el plano guardado antes de conservar una corrección.",
      saved: "Corrección guardada. Revisa el plano y luego apruébalo.",
      savedNeedsChecks: "Corrección guardada. Reintenta las comprobaciones antes de aprobar.",
      refreshed: "Comprobaciones actualizadas.",
      refreshFailed: "No se pudieron actualizar las comprobaciones. Inténtalo de nuevo.",
      unavailable:
        "No se pudieron cargar las comprobaciones. Reinténtalas antes de guardar o aprobar.",
      changed: "El plano guardado cambió · actualiza las comprobaciones.",
      needsRefresh: "Corrección guardada · hay que actualizar las comprobaciones.",
      retryBeforeApproval: "Reintenta las comprobaciones antes de guardar o aprobar.",
      saveAndApprove: "Guarda esta corrección y aprueba el plano guardado.",
      alreadyApproved: "Esta revisión guardada ya está aprobada.",
      previewOnly: "El guardado y la aprobación aún no están conectados en esta sesión.",
      approveAsIs:
        "Aprueba este plano guardado tal como está, incluidas sus comprobaciones pendientes.",
      keepAndApprove: "Conservar y aprobar (A)",
      unsavedManual:
        "Hay cambios manuales sin guardar. Conserva la corrección o vuelve al plano guardado antes de continuar.",
      returnToSaved: "Volver al plano guardado",
      backToMerchflow: "Volver al flujo",
      chooseMerchflow: "Elige un flujo para revisar sus planogramas.",
      store: "Tienda",
      planPosition: "Plano {{position}} de {{count}} en {{group}}",
      loadFailed: "No se pudo cargar la revisión",
      endOfGroup: " · Fin del grupo",
      queueFailed:
        "No se pudieron cargar estos planogramas. Se conservan tus decisiones de revisión.",
      retryQueue: "Reintentar carga de revisión",
      fixPreviewDetails: "Detalles de la vista previa de la corrección",
      previewUnsaved: "Solo vista previa · nada guardado.",
      comparedOriginal: "Comparado con el original.",
      changedBay: "Módulo modificado",
      dragHelp: "Arrastra productos para editar. Selecciona uno para ver más opciones.",
      wholePlan: "Plano completo",
      bay: "Módulo {{number}}",
      issueSelected: "Problema {{number}} seleccionado",
      dragToEdit: "Arrastrar para editar",
      beforeReadOnly: "Antes · solo lectura",
      editingControls: "Controles de edición del plano",
      reviewTools: "Herramientas de revisión",
      selectedShelfRule: "Regla seleccionada de ubicación en estante",
      currentCheck: "Comprobación actual",
      previousPlan: "Plano anterior",
      previousPlanKey: "Plano anterior (←)",
      nextPlan: "Siguiente plano",
      nextPlanKey: "Siguiente plano (→)",
      secondsPerPlan: "Segundos por plano",
      shortcuts: "Atajos",
      before: "Antes",
      after: "Después",
      savedPlan: "Plano guardado",
      savedLabel: "Guardado",
      product: "Producto",
      backToFixes: "Volver a las correcciones",
      autofixChecks: "Corrección automática y comprobaciones",
      fixWithAi: "Corregir con IA",
      shelfRule: "Regla de ubicación en estante",
      savingFix: "Guardando corrección…",
      keepFix: "Conservar esta corrección",
      saveThenApprove: "Guarda la corrección y luego aprueba.",
      attention: "¿Qué necesita atención?",
      flagHelp: "Marcado para seguimiento. Añade una nota o elige un motivo (opcional).",
      flagPlaceholder: "Describe qué necesita atención (opcional)",
      saveNote: "Guardar nota",
      unflag: "Quitar marca",
      approved: "Aprobado ✓",
      undoApproval: "Deshacer aprobación",
      undoLastApproval: "Deshacer última aprobación",
      saveApproveFix: "Guarda y aprueba esta corrección.",
      approvedEdit: "Plano aprobado · edítalo aquí para hacer otra corrección.",
      checksRemain: "Quedan comprobaciones · aprueba tal como está.",
      changeRemoved: "Retira {{count}} exhibiciones de productos de los estantes",
      changeAdded: "Coloca {{count}} exhibiciones de productos en los estantes",
      changeMoved: "Reubica {{count}} exhibiciones de productos",
      changeFacings: "Cambia los frentes de {{count}} exhibiciones de productos",
      changeCapping: "Cambia el apilado de {{count}} exhibiciones de productos",
      changeOther: "Cambian {{count}} exhibiciones de productos",
      changeLocation: "{{changes}} en {{location}}.",
      changedBayLabel: "módulo {{number}}",
      changedBaysLabel: "{{count}} módulos",
      chooseChangedBay: "Elige el módulo modificado",
      changedBayNumber: "Módulo modificado {{number}}",
      whatToCheck: "Qué comprobar",
      categoryOrder:
        "Los productos deben seguir la agrupación y secuencia acordadas para esta categoría.",
      showIssue: "Mostrar este problema",
      run: "Ejecución {{id}}",
      previewUnsavedBanner: "Vista previa · cambios sin guardar.",
      newerRun: "Ver ejecución más reciente {{id}}",
      unsavedEdit: "Edición sin guardar",
      manualEdit: "Edición manual",
      hideProductControls: "Ocultar controles de productos",
      productControls: "Controles de productos",
      undoEdit: "Deshacer edición",
      redoEdit: "Rehacer edición",
      reviewChanges: "Revisar cambios",
      doneEditing: "Terminar edición",
      discardEdits: "Descartar ediciones",
      manualHelp:
        "Arrastra productos para reordenarlos o moverlos a otro estante. Selecciona un producto para ver más opciones. Los cambios no se guardan hasta conservar o aprobar la corrección.",
      allPlans: "Todos los planos ({{count}})",
      dismiss: "Cerrar",
      approvalUndone: "Aprobación deshecha.",
      flagRemoved: "Marca eliminada.",
      refreshBeforeApproval:
        "Actualiza las comprobaciones de los planos guardados antes de aprobar.",
      undoApprovalFailed:
        "No se pudo deshacer la aprobación. Reintenta o vuelve a cargar el plano.",
      unflagFailed: "No se pudo quitar la marca. Inténtalo de nuevo.",
      savedReloadFailed:
        "Se guardó la corrección, pero no se pudieron cargar las comprobaciones. Reintenta antes de aprobar.",
      unchangedMove: "Ese movimiento no cambia el plano. Prueba con otro producto o estante.",
      speed: "Velocidad · {{seconds}} s por plano",
      stockTarget: "Por debajo del objetivo de existencias",
      stockCorePriorities: "Prioridades de existencias y surtido esencial",
      invalidPreview:
        "Esta vista previa de IA contiene datos incompletos y no se puede mostrar ni guardar. El plano guardado no cambió. Pide a la IA que lo intente de nuevo.",
      savedChecksUnavailable:
        "Las comprobaciones del plano guardado no están disponibles. Reinténtalas.",
      revisionUnconfirmed:
        "Se guardó la corrección pero no se pudo confirmar su revisión. Vuelve a cargar antes de aprobar.",
      approvalChanged: "El plano guardado cambió. Reintenta las comprobaciones antes de aprobar.",
      approvalFailed: "No se pudo guardar la aprobación. Inténtalo de nuevo.",
      approvalsChanged:
        "Los planos guardados cambiaron. Reintenta las comprobaciones antes de aprobar.",
      approvalsFailed: "No se pudieron guardar las aprobaciones. Inténtalo de nuevo.",
      wrongPlanFix: "La corrección automática pertenece a otro plano guardado. Vuelve a cargarlo.",
      wrongFixOption: "La corrección automática pertenece a otra opción. Inténtalo de nuevo.",
      queueChanged: "El plano guardado cambió; vuelve a cargar la cola antes de calificar.",
      outsideMerchflow: "La revisión recibió un plano fuera del flujo seleccionado.",
    },
    controls: {
      paused: "EN PAUSA",
      play: "Reproducir",
      pause: "Pausar",
      previous: "Anterior",
      next: "Siguiente",
      approve: "Aprobar",
      flag: "Marcar",
      describe: "Describir corrección",
      fullscreen: "Pantalla completa",
      exitFullscreen: "Salir de pantalla completa",
      dwell: "Permanencia {{seconds}} s",
      autoApprove: "Aprobar al terminar el tiempo",
      fixQueue: "Cola de correcciones ({{count}})",
      openEditor: "Abrir en el editor",
      retryChecks: "Reintentar comprobaciones",
      undoSavedFix: "Deshacer corrección guardada",
    },
    side: {
      score: "Puntaje estético",
      ungraded: "Sin calificar",
      bandFix: "menos de 70 · banda de corrección",
      bandReview: "70–89 · banda de revisión",
      bandAudit: "90+ · muestra de auditoría",
      reasons: "Por qué el puntaje lo dice",
      noReasons: "Nada marcado por el puntaje ni por el solver",
      loadingPlan: "Cargando el plano…",
      worstBlock: "Peor bloque",
      emptiestShelf: "Anaquel más vacío: corrida {{run}}, anaquel {{shelf}}, hueco de {{gap}} in",
      whyBad: "¿Por qué está mal?",
      describeTitle: "Describe la corrección",
      describePlaceholder:
        "p. ej. Mover las variantes de 2 litros al anaquel inferior y reagrupar por marca de izquierda a derecha.",
      save: "Guardar",
      cancel: "Cancelar",
      decided: "Decisión",
      states: {
        SCORED: "Aún no visto",
        APPROVED: "Aprobado",
        FLAGGED: "Marcado",
        DESCRIBED: "Corrección descrita",
        RECHECK: "Cambió desde la última decisión",
      },
    },
    view: {
      label: "Vista de comparación",
      hint: "Cómo se muestran el plano antes y después; V cambia. Se mantiene en todos los planos.",
      single: "Uno",
      stacked: "Antes / después",
      blink: "Alternar",
    },
    autofix: {
      label: "Autofix",
      on: "Activado",
      off: "Desactivado",
      onDetail:
        "Cada corrección segura se aplica a su plano en cuanto está lista. Aprobar la conserva; Deshacer devuelve el plano.",
      offDetail:
        "Autofix está desactivado en este merchflow: los planos quedan como se generaron y no se prepara nada nuevo.",
      changing: "Cambiando…",
      loading: "Leyendo Autofix…",
      loadFailed: "No se pudo saber si Autofix está activado.",
      retry: "Reintentar",
      approveHeld: "Autofix está cambiando. Vuelve a aprobar cuando termine.",
      approveStale:
        "Autofix cambió los planes y aún no se han vuelto a leer. Actualiza el estado y luego aprueba.",
      decisionsUnsaved:
        "No se pudieron guardar algunas decisiones de revisión, así que Autofix no se cambió.",
      confirm_one: "¿Deshacer {{count}} corrección aún sin aprobar?",
      confirm_other: "¿Deshacer {{count}} correcciones aún sin aprobar?",
      confirmDetail:
        "Los planos aprobados conservan sus correcciones. Al activar Autofix de nuevo, estas correcciones vuelven.",
      confirmOff_one: "Deshacer {{count}} corrección y desactivar",
      confirmOff_other: "Deshacer {{count}} correcciones y desactivar",
      turnedOff: "Autofix desactivado: {{count}} corrección(es) deshecha(s)",
      turnedOn: "Autofix activado: {{count}} corrección(es) de vuelta en sus planos",
      skipped: "{{count}} plano(s) cambiaron desde entonces y quedaron como están",
    },
    readyGroups: {
      title: "Aprobar los {{count}} planos listos, agrupados por el tipo de corrección",
      families: {
        "cdt-order": "Orden CDT corregido · {{count}}",
        blocks: "Bloques unidos · {{count}}",
        "shelf-positions": "Estantes movidos · {{count}}",
        other: "Otras correcciones · {{count}}",
        unchanged: "Sin cambios de Autofix · {{count}}",
      },
      kinds: {
        "cdt-order": "orden CDT",
        blocks: "bloques unidos",
        "shelf-positions": "estantes movidos",
        other: "otras correcciones",
        unchanged: "sin cambios",
      },
      plan: "{{store}} {{before}} → {{after}}",
      review: "Revisar este grupo",
      approve_one: "Aprobar {{count}}",
      approve_other: "Aprobar {{count}}",
      sameKind_one:
        "{{count}} plano más recibió el mismo tipo de corrección ({{kind}}) y está listo. ¿Aprobarlo también?",
      sameKind_other:
        "{{count}} planos más recibieron el mismo tipo de corrección ({{kind}}) y están listos. ¿Aprobarlos todos?",
      dismiss: "Descartar",
    },
    planAutofix: {
      leftAsIs: { title: "Autofix dejó este plano como estaba" },
      undone: {
        title: "Deshizo la corrección de Autofix",
        detail: "Puntaje {{before}} → {{after}} con la corrección. Rehacer la devuelve.",
      },
      working: {
        title: "Autofix está trabajando en este plano",
        detail: "Su corrección se aplica al plano en cuanto esté lista.",
      },
      off: {
        title: "Autofix está desactivado en este merchflow",
        detail:
          "Este plano queda como se generó. Active Autofix arriba para aplicar sus correcciones.",
      },
      redo: "Rehacer corrección",
      redone: "Corrección de vuelta en el plano",
      approveAsIs: "Aprobar tal cual (A)",
      notNeeded: "Nada en este plano es algo que Autofix corrija.",
      notLooked: "Autofix aún no ha revisado este plano.",
    },
    arrival: {
      changed: "Cambiados por Autofix · {{count}}",
      changedHint: "Aprobar conserva la corrección; Deshacer devuelve el plano.",
      changedStillStops:
        "{{count}} cambiados más aún tienen una tarjeta que impide aprobar: están en Le necesitan.",
      reviewChanged: "Revisar cambios",
      leftAsIs: "Sin cambios · {{count}}",
      leftAsIsDetail: "Autofix no cambió estos, y ninguna tarjeta impide aprobar.",
      needsYou: "Le necesitan · {{count}}",
      needsYouDetail: "Una tarjeta aún impide aprobar, con o sin corrección.",
      working: "Autofix aún trabaja en {{count}} plano(s); cada uno pasa a un grupo al terminar.",
      play: "Reproducir",
      startNeedsYou: "Empezar por estas",
      ready: "Listos para aprobar: {{count}} planos sin tarjetas que impidan aprobar",
      readyNone: "Listos para aprobar: ninguno todavía",
      noneYet: "Ninguno todavía",
      approveReady: "Aprobar todos los planos listos (conserva sus correcciones)",
      why: {
        fixedStillStops: "Cambiados, aún le necesitan",
        noChange: "Ningún cambio ayuda",
        breaksBlocks: "Rompería otros bloques",
        inPieces: "Partiría bloques en trozos",
        greyOnly: "Solo quita tarjetas que no impiden aprobar",
        lowersScore: "Bajaría la puntuación",
        addsProblem: "Añadiría un problema",
        breaksRule: "Rompería una regla obligatoria",
        undone: "Deshizo la corrección",
        autofixOff: "Autofix está desactivado",
        notForAutofix: "No es algo que Autofix corrija",
        failed: "No se pudo preparar",
        other: "Otros",
      },
    },
    reel: {
      before: "Antes · plano guardado",
      after: "Después · con la corrección",
      beforeOriginal: "Antes · plano original",
      afterApplied: "Después · plano guardado, corrección aplicada",
    },
    issues: {
      title: "Qué está mal",
      showOnPlan: "Mostrar",
      hideOnPlan: "Ocultar",
      ignore: "Ignorar",
      close: "Cerrar",
      pinHint: "Marca {{number}} en el plano",
      builtWith: "Como referencia ({{count}})",
      ignored: "{{count}} ignoradas en esta revisión",
      unignore: "Mostrar de nuevo",
      ignoredToast: "Ignorada en todos los planos de esta revisión",
      approvable: "No impide la aprobación",
      groups: {
        stops: "Impiden aprobar · {{count}}",
        look: "Vale la pena mirar · {{count}}",
        pieces: "Bloques en partes · {{count}}",
        split: "Bloques partidos en un estante · {{count}}",
        column: "Marcas fuera de su columna · {{count}}",
        ragged: "Bordes irregulares · {{count}}",
        onlyBlocking: "(impiden aprobar solo porque el bloqueo es {{score}} de {{bar}})",
        blockingNote:
          "El bloqueo es {{score}}, bajo su umbral de {{bar}}, así que cada tarjeta de bloque impide aprobar. Subir el bloqueo a {{bar}} las levanta todas.",
      },
      action: {
        applying: "Aplicando el cambio…",
        redo: "Rehacer corrección",
        redoWill: "Rehacer devuelve la corrección que deshizo: {{changes}}.",
        redoWillBare: "el plan tal como lo corrigió la autocorrección",
        editor: "Corregir en el editor",
        wouldSplit: "La autocorrección probó este plan: su cambio partiría {{blocks}} en trozos.",
        noChangeHelps: "La autocorrección lo intentó: ningún cambio posible mejora este plan.",
        wouldBreakOthers:
          "La autocorrección probó este plan: su cambio rompería otros bloques: {{blocks}}.",
        wouldAddProblem: "La autocorrección probó este plan: su cambio añadiría un problema.",
        wouldLowerScore: "La autocorrección probó este plan: su cambio bajaría la puntuación.",
        clearsOnlyMinor:
          "La autocorrección probó este plan: su cambio solo quitaría tarjetas que no impiden aprobar.",
        clearsNothing:
          "La autocorrección probó este plan: su cambio no quitaría ninguna tarjeta que impida aprobar ni subiría la puntuación.",
        wouldBreakRules:
          "La autocorrección probó este plan: conservar su cambio rompería las reglas de este planograma.",
        tried: "La autocorrección lo intentó y se detuvo sin encontrar un cambio seguro.",
        leftByFix:
          "La autocorrección corrigió lo que pudo en este plan y no encontró un cambio seguro para esta.",
        preparing:
          "La autocorrección aún trabaja en este plan; su cambio se aplica en cuanto esté listo.",
        autofixOff: "Autofix está desactivado en este merchflow.",
        noSafeFix: "La autocorrección no encontró un cambio seguro para esto.",
        planOptions:
          "Corrige las tarjetas de abajo una a una, marca el plan para una nueva resolución o apruébalo tal cual si la tarjeta es aceptable.",
      },
      approvableHint:
        "El bloqueo está en el umbral de aprobación o por encima, así que el plano puede aprobarse con este bloque tal como está. Se muestra para no ocultar nada.",
      titles: {
        overlap: "Productos superpuestos",
        pastEdge: "Fuera del borde del estante",
        emptyShelf: "Estante vacío",
        unplaced: "Productos fuera del plano",
        eliminated: "Marca eliminada",
        blockInPieces: "Bloque en pedazos",
        blockStaircased: "Columna de primer nivel con bordes irregulares",
        blockOutOfShape: "Bloque fuera de forma",
        bandBroken: "Franja de estante rota",
        notFull: "Estantes sin llenar",
        blockPieces: "{{block}}: en {{pieces}} pedazos",
        blockSplit: "{{block}}: dividido en un estante",
        blockRagged: "{{block}}: bordes irregulares",
        blockColumn: "{{block}}: fuera de su columna",
        cdt: "Fuera del orden del CDT",
        gap: "Hueco en un estante",
        rulesKept: "Reglas de estante rotas",
        ergonomics: "Envases pesados o grandes arriba",
        sizeVertical: "Envases pequeños bajo grandes",
        solver: "Relajada: {{rule}}",
        human: "Marcado por un revisor",
      },
      rules: {
        shelf_segment_space: "Segmento sin su espacio",
        facings_too_many: "Frentes sobre el tope",
        facings_not_enough: "Frentes bajo el mínimo",
        fixed_notch_num: "Estante fuera de su altura fija",
        cdt1_order: "Grupos de primer nivel fuera de orden",
        cdt2_order: "Grupos de segundo nivel fuera de orden",
        cdt3_order: "Grupos de tercer nivel fuera de orden",
        cdt1_contiguous: "Grupo de primer nivel dividido",
        cdt2_contiguous: "Grupo de segundo nivel dividido",
        cdt3_contiguous: "Grupo de tercer nivel dividido",
        item_span: "Frentes cruzan un límite",
        rows_not_enough: "Menos filas de las requeridas",
        min_distribution: "Bajo la distribución mínima",
        units_not_enough_dos: "Bajo sus días de inventario",
        units_not_enough_mos: "Bajo su mínimo en estante",
        units_too_many: "Unidades sobre el tope",
        min_width: "Bajo su ancho mínimo",
        item_id_order: "Fuera de la secuencia indicada",
      },
      where: {
        example: "{{example}}",
        exampleMore: "{{example}} +{{more}}",
        unplaced: "{{count}} fuera: {{names}}",
        names: "{{names}}",
        block: "Estantes {{shelves}}",
        blockRagged: "Estantes {{shelves}} · {{packs}} envases fuera",
        gap: "Módulo {{bay}}, estante {{shelf}} · {{gap}} {{unit}}",
        ordering: "{{block}} · {{count}} fuera de orden",
        blockOrder: "Bloques {{percent}}% en el orden del árbol",
        percent: "{{percent}}%",
        sizeVertical:
          "Módulo {{bay}} {{segment}}: {{lower}} {{unit}} en el estante {{lowerShelf}} bajo {{upper}} {{unit}}",
        solver: "{{names}} · módulo {{bay}}, estante {{shelf}}",
        solverCount: "{{count}} a nivel de estante",
        split: "{{group}} · {{runs}} tramos en módulo {{bay}}, estante {{shelf}}",
      },
      detail: {
        split: "Módulo {{bay}}, estante {{shelf}}: {{runs}} tramos",
        column: "Columna en estantes {{shelves}}, cede los estantes {{missing}} a otras marcas",
        rule: "{{name}} · estante {{shelf}} ({{band}})",
        sizeVertical:
          "Módulo {{bay}} {{segment}}: {{lower}} {{unit}} bajo {{upper}} {{unit}} (estante {{lowerShelf}})",
        more: "+{{count}} más",
      },
      shelfRule: {
        title: {
          prohibited: "No permitido en este estante: {{name}}",
          required: "Falta un grupo de productos obligatorio: {{name}}",
          reserved: "Producto incorrecto en una zona reservada: {{name}}",
        },
        location: "Módulo {{bay}}, estante {{shelf}} desde arriba",
        locations: "{{locations}}",
        unresolved: "El contexto guardado de la regla no permite identificar el estante exacto.",
        expected: {
          prohibited: "Esperado: los productos de “{{band}}” no deben estar en este estante.",
          required: "Esperado: esta zona debe contener productos de “{{band}}”.",
          reserved: "Esperado: esta zona está reservada únicamente para productos de “{{band}}”.",
        },
        actual: {
          placed: "Actual: {{name}} está colocado aquí. Frentes: {{facings}}.",
          missing: "Actual: no hay productos de “{{band}}” en la zona indicada.",
          unresolved:
            "La puntuación indicó {{name}} ({{facings}} frentes) en la fila {{shelf}}; el estante físico exacto no está disponible.",
        },
        shownSide: "Mostrando: {{side}}",
        resolvedTitle: "Regla de colocación cumplida",
        resolved:
          "La regla se cumple en el plano mostrado. No se resalta ningún producto infractor.",
        requiredPosition: "Posición obligatoria para este grupo: {{locations}}.",
      },
    },
    reasons: {
      "human:merchandising": "Sentido comercial",
      "human:brand-adjacency": "Adyacencia de marca",
      "human:product-image": "Imagen de producto incorrecta",
      "human:shopper-flow": "Flujo del comprador",
      "human:other": "Otro (requiere nota)",
      "audit:unsampled": "Banda de auditoría, aprobada en bloque",
      allRules: "todas las reglas relajadas",
      prefixes: {
        gate: "Puerta",
        aesthetics: "Puntaje",
        solver: "Solver",
        human: "Humano",
        audit: "Auditoría",
      },
    },
    auto: {
      didTitle: "Lo que el agente hizo en este plano",
      run: "Autocorregir este plano",
      changed: "Se escribió una corrección sobre este plano. El plano original sigue guardado.",
      revertOriginal: "Volver al plano original",
      all: "Autocorregir todo ({{count}})",
      allTitle: "Autocorrección",
      allProgress:
        "{{done}} de {{total}} planos · {{changed}} cambiados · {{unchanged}} sin cambios",
      allDone:
        "Hecho: {{done}} de {{total}} planos · {{changed}} cambiados y de vuelta en el reproductor para revisar · {{unchanged}} sin cambios",
      allFailed: "{{count}} no se pudieron leer o escribir",
      stop: "Detener",
      stopping: "Deteniendo tras este lote…",
    },
    proposals: {
      changes: {
        productsMoved_one: "mueve {{count}} producto",
        productsMoved_other: "mueve {{count}} productos",
        facingsChanged_one: "{{count}} frente",
        facingsChanged_other: "{{count}} frentes",
        shelvesMoved_one: "mueve {{count}} estante",
        shelvesMoved_other: "mueve {{count}} estantes",
      },
      showOriginal: "Ver el original",
      showApplied: "Ver el plano aplicado",
      undoKept: "Deshacer la propuesta conservada",
      retry: "Reintentar",
      approveBlocked:
        "Autofix aún trabaja en este plano: apruébelo cuando su corrección esté aplicada",
      kept: "Propuesta de autocorrección conservada",
      nothingSafe: "La autocorrección no encontró nada seguro: {{reason}}",
      prepared: "{{count}} plano(s) en cola para propuestas",
    },
    fixSummary: {
      title: "Autofix cambió este plano",
      score: "Puntaje",
      stops: "Tarjetas que impiden aprobar",
      hint: "La corrección está en el plano, aún sin aprobar.",
      approve: "Aprobar con la corrección (A)",
      undo: "Deshacer la corrección (U)",
      flag: "Marcar (F)",
      beforeAfter: "Antes / después (V)",
      effects: {
        "reorder-products_one": "reordenó {{count}} producto en su estante",
        "reorder-products_other": "reordenó {{count}} productos en sus estantes",
        "move-between-shelves_one": "movió {{count}} producto a otro estante",
        "move-between-shelves_other": "movió {{count}} productos a otro estante",
        "increase-facings_one": "añadió {{count}} frente",
        "increase-facings_other": "añadió {{count}} frentes",
        "decrease-facings_one": "quitó {{count}} frente",
        "decrease-facings_other": "quitó {{count}} frentes",
        "move-shelves_one": "movió {{count}} estante",
        "move-shelves_other": "movió {{count}} estantes",
        "reorient-products_one": "giró {{count}} producto",
        "reorient-products_other": "giró {{count}} productos",
        "resize-products_one": "cambió la compresión de {{count}} producto",
        "resize-products_other": "cambió la compresión de {{count}} productos",
        "add-products_one": "añadió {{count}} producto",
        "add-products_other": "añadió {{count}} productos",
        "remove-products_one": "quitó {{count}} producto",
        "remove-products_other": "quitó {{count}} productos",
        "join-blocks_one": "unió {{count}} bloque",
        "join-blocks_other": "unió {{count}} bloques",
      },
    },
    queue: {
      title: "Cola de correcciones",
      subtitle:
        "Marcas agrupadas por la corrección que las resuelve y su planograma base. Describe una corrección por grupo.",
      empty: "Nada marcado aún. Marca planogramas con F y se agrupan aquí por motivo.",
      cluster: "{{count}} planograma(s)",
      describeCluster: "Describe la corrección una vez para este grupo",
      saveCluster: "Guardar para {{count}}",
      approveUnsampled: "Aprobar banda de auditoría sin muestrear ({{count}})",
      approvedUnsampled: "Se aprobaron {{count}} planogramas de la banda de auditoría",
      skippedUnsampled:
        "{{count}} planograma(s) tienen una propuesta de Autofix pendiente: consérvala o descártala primero",
      merchflow: "Merchflow {{id}}",
      basePog: "POG base {{id}}",
      families: {
        fitBands: "Bandas de estante",
        sequenceByBase: "Secuenciar según la base",
        tidyBlocking: "Ordenar bloques",
        fillGaps: "Rellenar huecos",
      },
    },
    agent: {
      preview: "Previsualizar corrección en {{store}}",
      previewing: "Consultando al agente…",
      exemplar: "Probar en",
      allowCode: "Permitir código generado",
      prompt: "Lo que lee el agente",
      support: {
        catalog: "El agente puede hacerlo",
        codegen: "Requiere código generado",
        none: "El agente no puede corregirlo",
      },
      before: "Antes",
      after: "Después",
      changes: "Cambios",
      noChange: "El agente no cambió nada. Reformula la nota con códigos de producto y un anaquel.",
      warnings: "Advertencias",
      code: "Código generado",
      message: "El agente dijo",
      approve: "Aplicar a {{count}} planograma(s)",
      approveOne: "Solo este",
      reject: "No es lo que quise decir",
      rejectFeedback: "¿Qué estuvo mal? (opcional, se guarda con la ejecución)",
      manualFallback: "Dos previsualizaciones rechazadas. Corrige este grupo a mano.",
      applying: "Aplicando {{done}} de {{total}}…",
      stop: "Detener después de este lote",
      summary:
        "{{written}} escritos · {{skipped}} sin cambios · {{failed}} fallidos · {{stale}} cambiaron mientras tanto",
      retryFailed: "Reintentar fallidos ({{count}})",
      recheckHint:
        "Los planogramas escritos vuelven al reproductor para revisión. Nada se aprueba por ti.",
      status: {
        written: "escrito",
        skipped: "sin cambios",
        failed: "fallido",
        stale: "cambió mientras tanto",
      },
      diff: {
        moved: "movido",
        facings: "frentes",
        orientation: "orientación",
        squeeze: "compresión",
        shelf: "Módulo {{bay}} · anaquel {{shelf}}",
      },
      errors: {
        notConfigured: "El agente de POG no está configurado en este entorno.",
        handoverExpired: "El plan del agente expiró. Previsualiza la corrección de nuevo.",
        exemplarChanged: "Ese planograma cambió desde que lo viste. Previsualiza de nuevo.",
        sessionExpired: "Tu sesión expiró. Inicia sesión de nuevo y reintenta.",
        failed: "El agente no pudo hacerlo: {{detail}}",
      },
    },
    toasts: {
      approvedMany: "{{count}} planos aprobados",
      approved: "Aprobado",
      flagged: "Marcado",
      described: "Descripción de la corrección guardada",
      fullscreenUnavailable: "La pantalla completa no está disponible aquí",
    },
  },
  storeViewer: {
    title: "Visor de POG de tienda",
    readOnly: "Solo lectura",
    summary: "Tienda {{store}} · {{category}} · {{merchflow}}",
    summaryApproved: "Tienda {{store}} · {{category}} · {{merchflow}} · aprobado",
    previous: "Anterior",
    next: "Siguiente",
    position: "{{position}} de {{total}}",
    backToSpacePlanning: "Volver a Planificación de Espacios",
    highlight: "Resaltar",
    highlightModes: {
      none: "Ninguno",
      segment: "Segmento",
      brand: "Marca",
      variant: "Variante",
    },
    showImages: "Mostrar imágenes",
    showAvailableSpace: "Mostrar espacio disponible",
    loading: "Cargando el planograma de tienda...",
    loadFailed: "No se pudo cargar el planograma de tienda",
    notFound: "Planograma de tienda no encontrado",
    retry: "Reintentar",
    metrics: {
      coreRange: "Rango principal",
      shelfAlignment: "Alineación de estante",
      dosMos: "DOS / MOS",
    },
    salesSnapshot: "Ventas (instantánea del POG)",
    salesLatest: "Ventas (última carga)",
  },
  proofing: {
    canvas: {
      view: "Vista",
      focusBay: "Enfocar módulo",
      wholePlan: "Plan completo",
      bay: "Módulo {{number}}",
      fitPlan: "Ajustar plan",
      comparisonViews: "Vistas de comparación",
      comparePlans: "Comparar planes",
      single: "Vista única",
      singleHelp: "Elija Antes o Después para mostrar un plan a la vez.",
      stacked: "Mostrar ambos",
      stackedHelp: "Vea el antes y el después juntos, uno encima del otro.",
      blink: "Alternar",
      blinkHelp: "Alterna entre el plan guardado y el propuesto.",
      explanation: "Explicación de {{label}}",
      notReadyHint:
        "Seleccione un modo ahora; la comparación aparece cuando haya una corrección lista.",
      savedPlan: "Plan guardado",
      beforePhase: "ANTES",
      afterPhase: "DESPUÉS",
      before: "Antes",
      after: "Después",
      showSide: "Mostrar lado de la comparación",
      savedPlanLabel: "plan guardado",
      originalPlanLabel: "plan original",
      proposedFixLabel: "corrección propuesta",
      savedFixLabel: "corrección guardada",
    },
    strip: {
      label: "Revisar por problema",
      groupTitle:
        "Revise estos {{count}} planogramas juntos. Siguiente y Reproducir se mantienen dentro de {{group}}.",
      groupOne: "{{label}} · {{count}} plan",
      groupMany: "{{label}} · {{count}} planes",
    },
    autofix: {
      heading: "Autocorrección",
      on: "Autocorrección activada",
      off: "Autocorrección desactivada",
      options: "Opciones de autocorrección",
      layout: "Distribución",
      capping: "Añadir capping",
      cappingHelp:
        "El capping añade productos sobre las pilas existentes donde el espacio y las reglas del estante lo permiten.",
      isOff: "La autocorrección está desactivada.",
      checking: "Buscando correcciones…",
      failed: "La autocorrección no pudo terminar.",
      retry: "Reintentar autocorrección",
      levellingReady: "Nivelación de pilas lista",
      cappingReady: "Capping listo",
      layoutReady: "Vista previa de distribución lista",
      noFix: "No se encontró ninguna corrección verificada",
      partialSearch:
        "La búsqueda se detuvo antes de probar todas las opciones. Aún podría existir una corrección.",
      keep: "Conservar corrección",
      preview: "Previsualizar corrección",
      returnToSaved: "Volver al plan guardado",
      undo: "Deshacer autocorrección",
      fixWithAi: "Corregir con IA",
      nothingToKeep: "No hay ningún cambio automático que conservar.",
      planChanged: "El plan cambió desde esta corrección. Recargue antes de deshacer.",
      limitWidth: "Un estante ya supera su ancho utilizable.",
      limitDepth: "Algunos cambios superan la profundidad utilizable o los límites de stock.",
      limitGrouping:
        "Algunos cambios romperían la agrupación de productos u otras comprobaciones de distribución.",
    },
    scores: {
      metrics: {
        dosMos: "Puntuación de cobertura de stock",
        coreRange: "Surtido básico",
        returnOnSpace: "Rentabilidad del espacio",
        shelfAlignment: "Alineación de estantes",
        aesthetics: "Estética",
      },
      scorecard: "Tarjeta de puntuación del plan",
      daysOfSupply: "Días de suministro",
      daysOfSupplyHelp:
        "Cumplimiento de días de suministro y mínimo en estante por exhibición. El recuento prioritario suma el stock de todas las exhibiciones de cada producto para identificar faltantes; el porcentaje no es un número de días.",
      dosMosHelp:
        "Qué tan cerca está el stock de los objetivos de días/meses. Es una puntuación de cumplimiento, no un número de días.",
      showBelowMinimum: "Mostrar productos por debajo del mínimo de días de suministro",
      showMissingCore: "Mostrar productos del surtido básico que faltan",
      notConfigured: "No configurado",
      notAvailable: "No disponible",
      changeNotAvailable: "Cambio no disponible",
      noChange: "Sin cambios",
      points: "{{change}} puntos",
      percentagePoints: "{{change}} puntos porcentuales",
      belowMinimum: "{{count}} por debajo del mínimo",
      coreMissing: "Faltan {{count}} productos del surtido básico",
      before: "Antes",
      after: "Después",
      saved: "Guardado",
      fiveScores: "Cinco puntuaciones del plan",
      beforeAfterScores: "Puntuaciones antes y después",
      savedScores: "Puntuaciones del plan guardado",
      score: "Puntuación",
      change: "Cambio",
      changeInPoints: "Cambio en puntos",
      notAvailableForPlan: "— No disponible para este plan",
    },
  },
};
